import { compileShader, linkProgram } from './gl-program.helpers';
import { getStoreyElevationRange } from '@shared/helpers/design/storey.helpers';
import type { Storey } from '@shared/models/design/storey.model';

const FACADE_HEIGHT_GRID_STEP_METERS = 10;
const FACADE_HEIGHT_GRID_ALPHA = 0.18;
const MAX_FACADE_HEIGHT_GRID_LINES = 64;

export type FacadeSkyGlResources = {
  program: WebGLProgram;
  buffer: WebGLBuffer;
  aPosition: number;
  uGroundNdcY: WebGLUniformLocation;
  /** Optional only so a hot-reloaded helper can safely use old layer resources. */
  uGridNdcYs?: WebGLUniformLocation;
  uGridCount?: WebGLUniformLocation;
  uNdcPixelHeight?: WebGLUniformLocation;
};

const FACADE_SKY_VERTICES = new Float32Array([
  -1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1,
]);

/** Project a local elevation point into normalized device coordinates. */
export const projectFacadeLevelNdcY = (
  matrix: ArrayLike<number>,
  point: readonly [number, number, number],
): number | undefined => {
  const [x, y, z] = point;
  const clipY =
    (matrix[1] ?? 0) * x +
    (matrix[5] ?? 0) * y +
    (matrix[9] ?? 0) * z +
    (matrix[13] ?? 0);
  const clipW =
    (matrix[3] ?? 0) * x +
    (matrix[7] ?? 0) * y +
    (matrix[11] ?? 0) * z +
    (matrix[15] ?? 0);
  if (!Number.isFinite(clipW) || Math.abs(clipW) < Number.EPSILON) {
    return undefined;
  }
  return clipY / clipW;
};

/** Project and clamp the ground edge used to divide sky from ground. */
export const projectGroundLevelNdcY = (
  matrix: ArrayLike<number>,
  point: readonly [number, number, number],
): number => {
  const projected = projectFacadeLevelNdcY(matrix, point);
  if (projected === undefined) return -1;
  return Math.max(-1, Math.min(1, projected));
};

/** Global 10 m levels intersecting the combined vertical building span. */
export const resolveFacadeHeightGridElevations = (
  buildings: ReadonlyArray<readonly Storey[]>,
): number[] => {
  let lowest = Number.POSITIVE_INFINITY;
  let highest = Number.NEGATIVE_INFINITY;

  for (const readonlyStoreys of buildings) {
    const storeys = [...readonlyStoreys];
    for (const storey of storeys) {
      if (storey.shapes.length === 0) continue;
      const range = getStoreyElevationRange(storeys, storey);
      lowest = Math.min(lowest, range.bottom);
      highest = Math.max(highest, range.top);
    }
  }
  if (!Number.isFinite(lowest) || !Number.isFinite(highest)) return [];

  const first = Math.ceil(lowest / FACADE_HEIGHT_GRID_STEP_METERS);
  const last = Math.floor(highest / FACADE_HEIGHT_GRID_STEP_METERS);
  return Array.from(
    { length: Math.max(0, last - first + 1) },
    (_, index) => (first + index) * FACADE_HEIGHT_GRID_STEP_METERS,
  );
};

/** Build the facade background: solid ground below, sky gradient above. */
export const initFacadeSkyGl = (
  gl: WebGLRenderingContext,
): FacadeSkyGlResources => {
  const vertexShader = compileShader(
    gl,
    gl.VERTEX_SHADER,
    `
      precision highp float;
      attribute vec2 a_position;
      varying float v_ndc_y;

      void main() {
        v_ndc_y = a_position.y;
        gl_Position = vec4(a_position, 0.0, 1.0);
      }
    `,
  );
  const fragmentShader = compileShader(
    gl,
    gl.FRAGMENT_SHADER,
    `
      precision highp float;
      uniform float u_ground_ndc_y;
      uniform float u_grid_ndc_ys[${MAX_FACADE_HEIGHT_GRID_LINES}];
      uniform int u_grid_count;
      uniform float u_ndc_pixel_height;
      varying float v_ndc_y;

      void main() {
        vec3 ground_color = vec3(0.91);
        vec3 horizon_color = vec3(1.0);
        vec3 zenith_color = vec3(0.82);
        vec3 background_color;
        if (v_ndc_y <= u_ground_ndc_y) {
          background_color = ground_color;
        } else {
          float height = (v_ndc_y - u_ground_ndc_y) /
            max(0.0001, 1.0 - u_ground_ndc_y);
          float blend = smoothstep(0.0, 1.0, height);
          background_color = mix(horizon_color, zenith_color, blend);
        }

        float grid_coverage = 0.0;
        for (int i = 0; i < ${MAX_FACADE_HEIGHT_GRID_LINES}; i++) {
          if (i >= u_grid_count) break;
          float distance_to_line = abs(v_ndc_y - u_grid_ndc_ys[i]);
          grid_coverage = max(
            grid_coverage,
            1.0 - smoothstep(0.0, u_ndc_pixel_height, distance_to_line)
          );
        }
        vec3 color = mix(
          background_color,
          vec3(0.22),
          grid_coverage * ${FACADE_HEIGHT_GRID_ALPHA}
        );
        gl_FragColor = vec4(color, 1.0);
      }
    `,
  );
  const program = linkProgram(gl, vertexShader, fragmentShader);
  const buffer = gl.createBuffer();
  if (!buffer) {
    gl.deleteProgram(program);
    throw new Error('Failed to create facade sky WebGL buffer');
  }

  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, FACADE_SKY_VERTICES, gl.STATIC_DRAW);

  const aPosition = gl.getAttribLocation(program, 'a_position');
  const uGroundNdcY = gl.getUniformLocation(program, 'u_ground_ndc_y');
  const uGridNdcYs = gl.getUniformLocation(program, 'u_grid_ndc_ys[0]');
  const uGridCount = gl.getUniformLocation(program, 'u_grid_count');
  const uNdcPixelHeight = gl.getUniformLocation(program, 'u_ndc_pixel_height');
  if (
    aPosition < 0 ||
    !uGroundNdcY ||
    !uGridNdcYs ||
    !uGridCount ||
    !uNdcPixelHeight
  ) {
    gl.deleteBuffer(buffer);
    gl.deleteProgram(program);
    throw new Error('Failed to resolve facade sky shader locations');
  }

  return {
    program,
    buffer,
    aPosition,
    uGroundNdcY,
    uGridNdcYs,
    uGridCount,
    uNdcPixelHeight,
  };
};

export const drawFacadeSky = ({
  gl,
  resources,
  groundNdcY,
  gridNdcYs = [],
}: {
  gl: WebGLRenderingContext;
  resources: FacadeSkyGlResources;
  groundNdcY: number;
  gridNdcYs: readonly number[];
}): void => {
  const wasDepthTest = gl.isEnabled(gl.DEPTH_TEST);
  const wasBlend = gl.isEnabled(gl.BLEND);
  const wasCullFace = gl.isEnabled(gl.CULL_FACE);
  const wasDepthMask = gl.getParameter(gl.DEPTH_WRITEMASK) as boolean;

  gl.useProgram(resources.program);
  gl.bindBuffer(gl.ARRAY_BUFFER, resources.buffer);
  gl.enableVertexAttribArray(resources.aPosition);
  gl.vertexAttribPointer(resources.aPosition, 2, gl.FLOAT, false, 0, 0);
  gl.uniform1f(resources.uGroundNdcY, groundNdcY);
  if (
    resources.uGridNdcYs &&
    resources.uGridCount &&
    resources.uNdcPixelHeight
  ) {
    const visibleGridNdcYs = gridNdcYs.slice(0, MAX_FACADE_HEIGHT_GRID_LINES);
    gl.uniform1fv(resources.uGridNdcYs, new Float32Array(visibleGridNdcYs));
    gl.uniform1i(resources.uGridCount, visibleGridNdcYs.length);
    gl.uniform1f(resources.uNdcPixelHeight, 2 / gl.drawingBufferHeight);
  }

  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.BLEND);
  gl.disable(gl.CULL_FACE);
  gl.depthMask(false);
  gl.drawArrays(gl.TRIANGLES, 0, 6);

  gl.depthMask(wasDepthMask);
  if (wasDepthTest) gl.enable(gl.DEPTH_TEST);
  if (wasBlend) gl.enable(gl.BLEND);
  if (wasCullFace) gl.enable(gl.CULL_FACE);
};

export const disposeFacadeSkyGl = (
  gl: WebGLRenderingContext,
  resources: FacadeSkyGlResources,
): void => {
  gl.deleteBuffer(resources.buffer);
  gl.deleteProgram(resources.program);
};
